import React, { useState, useEffect } from 'react';
import { getHCLView } from '../../api/apiClient';
import {
    Box,
    Paper,
    Typography,
    Button,
    Select,
    MenuItem,
    Stack,
    Divider,
    CircularProgress,
    Alert
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';

const renderContextParagraph = (context, type) => {
    if (!context) return null;

    if (type === 'business') {
        const users = Array.isArray(context.primary_users) && context.primary_users.length ? context.primary_users.join(' and ') : (context.primary_users || 'our primary users');
        const interfaces = Array.isArray(context.screens_or_interfaces) && context.screens_or_interfaces.length ? context.screens_or_interfaces.join(', ') : 'the main application screens';
        const workflow = Array.isArray(context.main_workflow) ? context.main_workflow.join(' -> ') : (context.main_workflow || 'a standard flow');
        const features = Array.isArray(context.key_features) && context.key_features.length ? context.key_features.join(', ') : 'essential functionalities';
        const reports = Array.isArray(context.reports) && context.reports.length ? context.reports.join(' and ') : 'helpful metrics';
        const roles = Array.isArray(context.user_roles) && context.user_roles.length ? context.user_roles.join(', ') : 'standard roles';

        return (
            <Typography variant="body2" sx={{ lineHeight: 1.7, color: 'text.secondary' }}>
                {context.summary} We're building this experience primarily for {users} (with access separated by {roles}), who will mainly interact with {interfaces}.
                To ensure everything runs smoothly, we've designed a clear journey that walks them through {workflow}.
                The core magic powering this includes {features}, which will also help generate {reports} to keep everyone informed.
                Right now, the project's overall status is looking good and is marked as '{context.status}'.
            </Typography>
        );
    }

    const architecture = context.architecture || 'a reliable architecture';
    const pattern = context.pattern || 'custom';
    const inputs = Array.isArray(context.inputs) && context.inputs.length ? context.inputs.join(', ') : 'various required data points';
    const outputs = Array.isArray(context.outputs) && context.outputs.length ? context.outputs.join(', ') : 'the expected system responses';
    const source = Array.isArray(context.data_source) && context.data_source.length ? context.data_source.join(', ') : (context.data_source || 'our secure databases');
    const storage = Array.isArray(context.storage_requirements) && context.storage_requirements.length ? context.storage_requirements.join(', ') : (context.storage_requirements || 'standard storage');
    const access = Array.isArray(context.access_method) ? context.access_method.join('/') : (context.access_method || 'the web');
    const security = context.security || 'industry standard protocols';
    const integrations = Array.isArray(context.integrations) && context.integrations.length ? context.integrations.join(', ') : 'no external third-party services yet';
    const models = Array.isArray(context.data_models) && context.data_models.length ? context.data_models.join(', ') : 'the core structural entities';

    return (
        <Typography variant="body2" sx={{ lineHeight: 1.7, color: 'text.secondary' }}>
            From a technical perspective, we are laying the foundation using {architecture}, built strongly around a {pattern} design pattern.
            The system is configured to securely process {inputs} and reliably output {outputs}.
            Under the hood, data will be safely sourced from {source} and retained in {storage}.
            Users will connect over {access}, safeguarded by {security}.
            We're currently bridging connections with {integrations}, and the whole structure is anchored by our underlying data models: {models}.
        </Typography>
    );
};

export default function HCLLiteView({ sessionId, tenantId }) {
    const [role, setRole] = useState('BUSINESS');
    const [viewData, setViewData] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const fetchView = async () => {
            setLoading(true);
            try {
                const data = await getHCLView(sessionId, role, tenantId);
                setViewData(data);
            } catch (err) {
                console.error(err);
            } finally {
                setLoading(false);
            }
        };

        if (sessionId) {
            fetchView();
        }
    }, [role, sessionId, tenantId]);

    return (
        <Paper variant="outlined" sx={{ p: 3, mt: 4, borderColor: "primary.main", bgcolor: "#f8f9ff" }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
                <AutoAwesomeIcon color="primary" />
                <Typography variant="h6" color="primary" fontWeight={700}>
                    HCL-Lite View
                </Typography>
            </Stack>

            <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
                <Select
                    size="small"
                    value={role}
                    onChange={e => setRole(e.target.value)}
                    sx={{ minWidth: 200, bgcolor: 'white' }}
                    disabled={loading}
                >
                    <MenuItem value="BUSINESS">Business Role</MenuItem>
                    <MenuItem value="DEVELOPER">Developer Role</MenuItem>
                </Select>
                {loading && <CircularProgress size={24} />}
            </Stack>

            {viewData && (
                <Box sx={{ mb: 4 }}>
                    <Divider sx={{ mb: 3 }} />
                    <Paper variant="outlined" sx={{ p: 2, bgcolor: 'white' }}>
                        <Typography variant="subtitle1" color={role === 'BUSINESS' ? "primary.main" : "secondary.main"} fontWeight={600} gutterBottom>
                            {role === 'BUSINESS' ? 'Business View' : 'Developer View'}
                        </Typography>
                        {renderContextParagraph(
                            role === 'BUSINESS' ? (viewData.business_view || viewData.side_by_side?.business) : (viewData.technical_view || viewData.side_by_side?.technical),
                            role === 'BUSINESS' ? 'business' : 'technical'
                        )}
                    </Paper>
                </Box>
            )}
        </Paper>
    );
}
